import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, ArrowRight, ArrowLeft, Check, Wand2 } from "lucide-react";
import { BackBar } from "../components/BackBar";

// Question definitions (FR + EN)
const QUESTIONS = [
  {
    key: "tier",
    fr: "Quel est le niveau actuel de votre enfant ?",
    en: "What is your child's current level?",
    options: [
      { value: "elementary", fr: "Élémentaire (GS – CM2)", en: "Elementary (K – Grade 5)" },
      { value: "middle", fr: "Collège (6e – 3e)", en: "Middle School (Grades 6 – 9)" },
      { value: "high", fr: "Lycée (2nde – Terminale)", en: "High School (Grades 10 – 12)" },
    ],
  },
  {
    key: "english",
    fr: "Quel est son niveau d'anglais aujourd'hui ?",
    en: "What's their current English level?",
    options: [
      { value: "native", fr: "Natif / quasi natif", en: "Native / near-native" },
      { value: "advanced", fr: "Avancé — à l'aise à l'oral et à l'écrit", en: "Advanced — fluent oral & written" },
      { value: "intermediate", fr: "Intermédiaire — comprend, s'exprime simplement", en: "Intermediate — understands, expresses simply" },
      { value: "beginner", fr: "Débutant — peu d'exposition", en: "Beginner — limited exposure" },
    ],
  },
  {
    key: "goal",
    fr: "Quel est votre objectif principal ?",
    en: "What is your main goal?",
    options: [
      { value: "diploma", fr: "Obtenir un US High School Diploma", en: "Earn a US High School Diploma" },
      { value: "bilingual", fr: "Bâtir un véritable bilinguisme académique", en: "Build true academic bilingualism" },
      { value: "maintain", fr: "Maintenir le niveau d'anglais déjà acquis", en: "Maintain English already acquired" },
      { value: "build", fr: "Construire l'anglais à partir de zéro ou presque", en: "Build English from scratch (or close)" },
    ],
  },
];

function recommend({ tier, english, goal }) {
  // Track logic
  const fluentScore = (english === "native" || english === "advanced" ? 2 : 0)
    + (goal === "diploma" || goal === "maintain" ? 1 : 0);
  const track = fluentScore >= 2 ? "fluent" : "bridge";

  // Program suggestion — aligned with the two Tracks
  let program = "bridge_track";
  if (goal === "diploma") program = "diploma";
  else if (track === "fluent" || goal === "maintain") program = "fluent_track";
  else program = "bridge_track";

  // Pricing — flat tuition across all grades
  const annualPrice = 1995;
  const tierLabelFr = tier === "elementary" ? "GS – CM2" : tier === "middle" ? "6e – 3e" : "2nde – Terminale";
  const tierLabelEn = tier === "elementary" ? "K – Grade 5" : tier === "middle" ? "Grades 6 – 9" : "Grades 10 – 12";

  return { track, program, annualPrice, tierLabelFr, tierLabelEn, tier };
}

export default function PathwayRecommender() {
  const { lang, t } = useLang();
  const isFR = lang === "fr";
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const total = QUESTIONS.length;
  const isResult = step === total;
  const result = useMemo(() => (isResult ? recommend(answers) : null), [isResult, answers]);

  const select = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const restart = () => { setAnswers({}); setStep(0); };

  const goToInquiry = () => {
    const params = new URLSearchParams({
      track: result.track,
      program: result.program,
      grade_band: result.tier,
    });
    nav(`/inquiry?${params.toString()}`);
  };

  return (
    <div className="bg-paper relative overflow-hidden min-h-[80vh]" data-testid="recommender-page">
      <div className="brand-blob" style={{ background: "hsl(var(--brand-mint))", width: 320, height: 320, top: 100, left: -120, opacity: 0.4 }} />
      <div className="brand-blob" style={{ background: "hsl(var(--brand-yellow))", width: 280, height: 280, top: 400, right: -100, opacity: 0.3 }} />

      <div className="max-w-3xl mx-auto px-6 lg:px-12 pt-8 pb-24 relative">
        <BackBar to="/" fallbackLabelFr="Accueil" fallbackLabelEn="Home" />
        <div className="flex items-center gap-2 mb-4">
          <span className="brand-pill">
            <Wand2 className="w-3.5 h-3.5" />
            {isFR ? "Pathway Recommender" : "Pathway Recommender"}
          </span>
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-4">
          {isFR ? "Trouvez la voie qui correspond à votre enfant." : "Find the track that fits your child."}
        </h1>
        <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">
          {isFR
            ? "Trois questions, deux minutes. Nous suggérons Fluent Track ou Bridge Track, le programme adapté et le tarif estimé — puis vous pouvez pré-remplir une demande."
            : "Three questions, two minutes. We'll suggest Fluent or Bridge Track, the matching program and estimated tuition — then you can pre-fill an inquiry."}
        </p>

        {/* Progress */}
        {!isResult && (
          <div className="mb-8 flex items-center gap-2" data-testid="recommender-progress">
            {QUESTIONS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-primary" : i === step ? "bg-accent" : "bg-border"}`} />
            ))}
            <span className="text-xs font-semibold text-muted-foreground ml-3 tabular-nums">{Math.min(step + 1, total)}/{total}</span>
          </div>
        )}

        {/* Question */}
        {!isResult && (
          <Card data-testid={`question-${step}`} className="border-2">
            <CardContent className="p-8">
              <p className="overline mb-4">{isFR ? `Question ${step + 1}` : `Question ${step + 1}`}</p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">
                {QUESTIONS[step][isFR ? "fr" : "en"]}
              </h2>
              <div className="space-y-2">
                {QUESTIONS[step].options.map((opt) => {
                  const active = answers[QUESTIONS[step].key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      data-testid={`option-${QUESTIONS[step].key}-${opt.value}`}
                      onClick={() => select(QUESTIONS[step].key, opt.value)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between group ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-secondary/40"
                      }`}
                    >
                      <span className="font-medium text-sm sm:text-base">{opt[isFR ? "fr" : "en"]}</span>
                      <ArrowRight className={`w-4 h-4 transition-all ${active ? "text-primary" : "text-muted-foreground opacity-0 group-hover:opacity-100"}`} />
                    </button>
                  );
                })}
              </div>
              {step > 0 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  data-testid="back-step"
                  className="mt-6 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {isFR ? "Retour" : "Back"}
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {isResult && result && (
          <div data-testid="recommender-result" className="space-y-6">
            <Card className="border-2 border-primary overflow-hidden">
              <div className={`p-8 ${result.track === "fluent" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-80">{isFR ? "Voie recommandée" : "Recommended track"}</span>
                </div>
                <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
                  {result.track === "fluent" ? "Fluent Track" : "Bridge Track"}
                </h2>
                <p className={`mt-3 max-w-md ${result.track === "fluent" ? "text-white/85" : "text-muted-foreground"}`}>
                  {result.track === "fluent"
                    ? (isFR
                        ? "Programme académique américain complet en anglais — pour les profils déjà solides en anglais."
                        : "Full American academic curriculum in English — for already-strong English profiles.")
                    : (isFR
                        ? "Approche progressive et personnalisée — de la langue à l'académique en anglais."
                        : "Progressive, personalized approach — from language to academic English.")}
                </p>
              </div>
              <CardContent className="p-8 grid sm:grid-cols-3 gap-4">
                <ResultStat label={isFR ? "Niveau" : "Level"} value={isFR ? result.tierLabelFr : result.tierLabelEn} />
                <ResultStat label={isFR ? "Programme suggéré" : "Suggested program"} value={t(`programs.${result.program}`)} />
                <ResultStat label={isFR ? "Tarif annuel" : "Annual tuition"} value={`${result.annualPrice.toLocaleString("fr-FR")} €`} accent />
              </CardContent>
            </Card>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{isFR ? "Inclus" : "Included"}</div>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{isFR ? "1h30 / semaine, 32 semaines" : "1h30 / week, 32 weeks"}</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{isFR ? "Petites classes (4–8 élèves)" : "Small classes (4–8 students)"}</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{isFR ? "Professeurs natifs anglophones" : "Native English teachers"}</li>
                  {result.tier === "high" && <li className="flex gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{isFR ? "+ 1h30 American History (option diplôme)" : "+ 1h30 American History (diploma option)"}</li>}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{isFR ? "Reconnaissance" : "Recognition"}</div>
                <Badge className="bg-accent/10 text-accent border-0 mb-3">COGNIA</Badge>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {isFR
                    ? "Diplôme américain accrédité, reconnu par les universités françaises et internationales."
                    : "Accredited American diploma, recognized by French and international universities."}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button data-testid="result-prefill-inquiry" size="lg" onClick={goToInquiry} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 shadow-md">
                {isFR ? "Pré-remplir une demande d'informations" : "Pre-fill an information request"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Link to="/register"><Button data-testid="result-signup" size="lg" variant="outline" className="rounded-full px-7 border-2">{t("nav.signup")}</Button></Link>
              <button data-testid="result-restart" onClick={restart} className="text-sm text-muted-foreground hover:text-foreground self-center inline-flex items-center gap-1.5 ml-2">
                <ArrowLeft className="w-3.5 h-3.5" />
                {isFR ? "Recommencer" : "Restart"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{label}</div>
      <div className={`font-display font-bold text-xl tracking-tight ${accent ? "text-accent" : ""}`}>{value}</div>
    </div>
  );
}
